import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import UserAvatar, { UserAvatarGroup } from '../components/UserAvatar';
import AICopilotPanel from '../components/AICopilotPanel';
import ReviewApplyDialog from '../components/ReviewApplyDialog';
import L10VideoPanel from '../components/L10VideoPanel';
import AudioCapture from '../components/AudioCapture';
import AutoApplySettings from '../components/AutoApplySettings';
import SuggestionQueue from '../components/SuggestionQueue';
import L10SectionContent from '../components/L10SectionContent';
import { 
  Users, Clock, PlayCircle, PauseCircle, CheckCircle2, 
  MessageSquare, ListChecks, Target, TrendingUp, Bell, 
  ArrowLeft, Zap, Minimize2, Maximize2, Loader2, Crown, 
  SkipForward, ChevronRight, ChevronLeft, Hand, Eye, UserCheck,
  Video, VideoOff, Mic, MicOff, Monitor, Phone,
  Sparkles, Brain, FileText, Lightbulb, Bot, RefreshCw,
  AlertTriangle, Check, X, Edit2, Mail, Shield, Circle,
  PanelRight, PanelRightClose, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EOSL10MeetingRoom = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  
  // Meeting state
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Timer state
  const [overallTimer, setOverallTimer] = useState(0);
  const [sectionTimer, setSectionTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Section state
  const [currentSection, setCurrentSection] = useState('segue');
  const [currentPresenterIndex, setCurrentPresenterIndex] = useState(0);
  const [sectionData, setSectionData] = useState({
    segue: { personal_wins: [], professional_wins: [] },
    scorecard: { metrics: [] },
    rocks: { rocks: [] },
    headlines: { items: [] },
    todos: { items: [] },
    ids: { issues: [] },
    conclude: { ratings: [], cascade_message: '' }
  });
  
  // Section notes state
  const [sectionNotes, setSectionNotes] = useState({});
  const [typedNotes, setTypedNotes] = useState('');
  
  // User role state
  const [currentUser, setCurrentUser] = useState(null);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [isFacilitator, setIsFacilitator] = useState(false);
  const [isObserver, setIsObserver] = useState(false);
  
  // Presenters
  const [presenters, setPresenters] = useState([]);
  const [completedPresenters, setCompletedPresenters] = useState([]);
  
  // AI Copilot state
  const [copilotEnabled, setCopilotEnabled] = useState(false);
  const [copilotConsent, setCopilotConsent] = useState(false);
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const [aiSectionNotes, setAiSectionNotes] = useState({});
  const [aiPrompts, setAiPrompts] = useState({ opening: [], time_warning: [], nudges: [] });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [suggestedUpdates, setSuggestedUpdates] = useState([]);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showUpdatesDialog, setShowUpdatesDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailPayload, setEmailPayload] = useState(null);
  
  // Video state
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(true);
  const [videoStatus, setVideoStatus] = useState(null);
  const [localVideo, setLocalVideo] = useState(true);
  const [localAudio, setLocalAudio] = useState(true);
  const [videoJoined, setVideoJoined] = useState(false);
  const [videoNotes, setVideoNotes] = useState([]);
  const [showVideoPanel, setShowVideoPanel] = useState(true);
  
  // Phase B: Transcription state
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [transcriptions, setTranscriptions] = useState([]);
  
  // Phase C: Auto-apply state
  const [autoApplyMode, setAutoApplyMode] = useState('disabled');
  const [showAutoApplySettings, setShowAutoApplySettings] = useState(false);
  
  // Tangent alert
  const [tangentWarning, setTangentWarning] = useState(false);
  
  // Refs
  const timerIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // Agenda sections
  const agendaSections = [
    { key: 'segue', label: 'Segue', icon: Users, defaultDuration: 5, color: 'bg-blue-500' },
    { key: 'scorecard', label: 'Scorecard Review', icon: TrendingUp, defaultDuration: 5, color: 'bg-green-500' },
    { key: 'rocks', label: 'Rock Review', icon: Target, defaultDuration: 5, color: 'bg-purple-500' },
    { key: 'headlines', label: 'Headlines', icon: MessageSquare, defaultDuration: 5, color: 'bg-yellow-500' },
    { key: 'todos', label: 'To-Do List', icon: ListChecks, defaultDuration: 5, color: 'bg-cyan-500' },
    { key: 'ids', label: 'IDS', icon: Zap, defaultDuration: 60, color: 'bg-red-500' },
    { key: 'conclude', label: 'Conclude', icon: CheckCircle2, defaultDuration: 5, color: 'bg-emerald-500' }
  ];

  // Get current user
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setCurrentUser(JSON.parse(userStr));
  }, []);

  useEffect(() => {
    fetchMeeting();
    fetchSectionData();
    
    pollIntervalRef.current = setInterval(() => {
      if (!isPaused) fetchMeetingState();
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [meetingId]);

  // Timer effect
  useEffect(() => {
    if (isTimerRunning && !isPaused) {
      timerIntervalRef.current = setInterval(() => {
        setOverallTimer(prev => prev + 1);
        setSectionTimer(prev => {
          const section = agendaSections.find(s => s.key === currentSection);
          const maxTime = section ? section.defaultDuration * 60 : 300;
          
          if (prev >= maxTime * 0.8 && prev < maxTime * 0.8 + 1) {
            setTangentWarning(true);
            setTimeout(() => setTangentWarning(false), 5000);
          }
          
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isTimerRunning, isPaused, currentSection]);
  
  // Fetch AI prompts when section changes
  useEffect(() => {
    if (currentSection && copilotEnabled) {
      fetchAIPrompts(currentSection);
    }
  }, [currentSection, copilotEnabled]);

  const fetchMeeting = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}`);
      const meetingData = response.data;
      setMeeting(meetingData);
      
      const participants = meetingData.participants || [];
      setPresenters(participants.filter(p => p.role !== 'observer'));
      
      if (currentUser) {
        const userParticipant = participants.find(p => p.user_id === currentUser.user_id);
        setIsObserver(userParticipant?.role === 'observer');
        setIsFacilitator(meetingData.facilitator_id === currentUser.user_id);
      }
      
      if (meetingData.current_section) setCurrentSection(meetingData.current_section);
      if (meetingData.current_presenter_index !== undefined) setCurrentPresenterIndex(meetingData.current_presenter_index);
      
      const agenda = meetingData.agenda || {};
      const sectionInfo = agenda[meetingData.current_section] || {};
      setCompletedPresenters(sectionInfo.completed_presenters || []);
      
      // AI Copilot status
      setCopilotEnabled(meetingData.ai_copilot_enabled || false);
      setCaptureEnabled(meetingData.capture_enabled || false);
      if (meetingData.ai_section_notes) setAiSectionNotes(meetingData.ai_section_notes);
      if (meetingData.ai_summary) setAiSummary(meetingData.ai_summary);
      
      // Video status
      setVideoEnabled(meetingData.video_enabled || false);
      
      if (meetingData.status === 'scheduled') {
        await startMeeting();
      } else if (meetingData.status === 'in_progress') {
        setIsTimerRunning(true);
      }
      
      await fetchVideoStatus();
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error('Failed to load meeting');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMeetingState = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}/state`);
      const state = response.data;
      
      setCurrentSection(state.current_section);
      setCurrentPresenterIndex(state.current_presenter_index);
      setIsMyTurn(state.is_my_turn);
      setIsFacilitator(state.is_facilitator);
      
      const agenda = state.agenda || {};
      const sectionInfo = agenda[state.current_section] || {};
      setCompletedPresenters(sectionInfo.completed_presenters || []);
    } catch (error) {
      console.error('Error fetching meeting state:', error);
    }
  };

  const fetchSectionData = async () => {
    try {
      const [liveScorecard, rocks, headlines, todos, issues] = await Promise.all([
        axios.get(`${API}/eos/live-scorecard`),
        axios.get(`${API}/eos/rocks`),
        axios.get(`${API}/eos/headlines`),
        axios.get(`${API}/eos/todos`),
        axios.get(`${API}/eos/issues`)
      ]);

      setSectionData(prev => ({
        ...prev,
        scorecard: { 
          metrics: liveScorecard.data.metrics,
          summary: liveScorecard.data.summary,
          period: liveScorecard.data.period,
          diagnostics: liveScorecard.data.diagnostics
        },
        rocks: { rocks: rocks.data },
        headlines: { items: headlines.data },
        todos: { items: todos.data },
        ids: { issues: issues.data }
      }));
    } catch (error) {
      console.error('Error fetching section data:', error);
    }
  };
  
  const fetchVideoStatus = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}/video/status`);
      setVideoStatus(response.data);
      setVideoEnabled(response.data.video_enabled);
    } catch (error) {
      console.error('Error fetching video status:', error);
    }
  };
  
  const fetchAIPrompts = async (section) => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}/copilot/facilitator-prompts?section=${section}`);
      setAiPrompts(response.data.prompts || { opening: [], time_warning: [], nudges: [] });
    } catch (error) {
      console.error('Error fetching AI prompts:', error);
    }
  };

  const startMeeting = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/start`);
      setMeeting(response.data);
      setIsTimerRunning(true);
      toast.success('Meeting started!');
    } catch (error) {
      console.error('Error starting meeting:', error);
      toast.error('Failed to start meeting');
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
    toast.info(isPaused ? 'Meeting resumed' : 'Meeting paused');
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  // AI Copilot functions
  const handleEnableCopilot = async () => {
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/copilot/enable`, {
        ai_copilot_enabled: true,
        consent_recorded: true,
        capture_enabled: false
      });
      setCopilotEnabled(true);
      setCopilotConsent(true);
      toast.success('AI Copilot enabled');
    } catch (error) {
      console.error('Error enabling copilot:', error);
      toast.error('Failed to enable AI Copilot');
    }
  };
  
  const handleGenerateSectionNotes = async () => {
    if (!copilotEnabled) {
      toast.error('Enable AI Copilot first');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/copilot/section-notes`, {
        section: currentSection,
        section_data: sectionData[currentSection] || {},
        typed_notes: typedNotes
      });
      
      setAiSectionNotes(prev => ({
        ...prev,
        [currentSection]: response.data
      }));
      
      toast.success('AI notes generated');
    } catch (error) {
      console.error('Error generating AI notes:', error);
      toast.error('Failed to generate AI notes');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleGenerateExecutiveSummary = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/copilot/executive-summary`);
      setAiSummary(response.data);
      setShowSummaryDialog(true);
      toast.success('Executive summary generated');
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error('Failed to generate summary');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleGenerateSuggestedUpdates = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/copilot/suggested-updates`);
      setSuggestedUpdates(response.data.suggestions || []);
      setShowUpdatesDialog(true);
      toast.success('Suggested updates generated');
    } catch (error) {
      console.error('Error generating updates:', error);
      toast.error('Failed to generate updates');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleApplyUpdate = async (updateId, action, editedData = null) => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/copilot/apply-update/${updateId}`, {
        action,
        edited_data: editedData
      });
      
      // Update local state
      setSuggestedUpdates(prev => prev.map(u => 
        u.id === updateId ? { ...u, status: action } : u
      ));
      
      toast.success(`Update ${action === 'approve' ? 'applied' : action}`);
    } catch (error) {
      console.error('Error applying update:', error);
      toast.error('Failed to apply update');
    }
  };
  
  const handlePrepareEmail = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/email-summary/prepare`);
      setEmailPayload(response.data);
      setShowEmailDialog(true);
      toast.success('Email summary prepared');
    } catch (error) {
      console.error('Error preparing email:', error);
      toast.error('Failed to prepare email - generate executive summary first');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleSendEmail = async () => {
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/email-summary/send`);
      toast.success('Email summary queued for delivery');
      setShowEmailDialog(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to queue email');
    }
  };

  // Video controls
  const handleEnableVideo = async () => {
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/video/enable`);
      setVideoEnabled(true);
      toast.success('Video enabled');
      await fetchVideoStatus();
    } catch (error) {
      console.error('Error enabling video:', error);
      toast.error('Failed to enable video');
    }
  };
  
  const handleJoinVideo = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/video/join`);
      setVideoJoined(true);
      if (response.data.is_mock) {
        toast.info('Video connected (mock mode)');
      } else if (response.data.join_url) {
        // For real providers, still show embedded panel but also open provider
        toast.success('Video connected');
      }
      // Fetch video notes
      await fetchVideoNotes();
    } catch (error) {
      console.error('Error joining video:', error);
      toast.error('Failed to join video');
    }
  };
  
  const handleLeaveVideo = async () => {
    try {
      setVideoJoined(false);
      toast.info('Left video call');
    } catch (error) {
      console.error('Error leaving video:', error);
    }
  };
  
  const fetchVideoNotes = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}/video-notes`);
      setVideoNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching video notes:', error);
    }
  };
  
  const handleVideoNotesUpdate = (notes) => {
    setVideoNotes(notes);
  };

  // Presenter flow
  const handleNextPerson = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/flow/next-person`);
      if (response.data.section_complete) {
        toast.success('All presenters complete!');
      } else {
        setCurrentPresenterIndex(response.data.current_presenter_index);
        toast.info('Next presenter');
      }
      setCompletedPresenters(response.data.completed_presenters || []);
      await fetchMeetingState();
    } catch (error) {
      toast.error('Failed to move to next presenter');
    }
  };
  
  const handleSkipPerson = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/flow/skip-person`);
      setCurrentPresenterIndex(response.data.current_presenter_index);
      toast.info('Skipped');
      await fetchMeetingState();
    } catch (error) {
      toast.error('Failed to skip');
    }
  };

  const goToNextSection = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/flow/next-section`);
      if (response.data.is_last_section) {
        toast.info('Final section');
      } else {
        setCurrentSection(response.data.current_section);
        setCurrentPresenterIndex(0);
        setSectionTimer(0);
        setCompletedPresenters([]);
        setTypedNotes('');
        toast.success(`Moved to ${response.data.current_section}`);
      }
    } catch (error) {
      toast.error('Failed to change section');
    }
  };

  const goToPreviousSection = async () => {
    try {
      const response = await axios.post(`${API}/eos/meetings/${meetingId}/flow/previous-section`);
      setCurrentSection(response.data.current_section);
      setCurrentPresenterIndex(0);
      setSectionTimer(0);
      setTypedNotes('');
    } catch (error) {
      toast.error('Failed to change section');
    }
  };

  const completeMeeting = async () => {
    try {
      if (copilotEnabled) {
        await handleGenerateExecutiveSummary();
        await handleGenerateSuggestedUpdates();
      }
      
      await axios.post(`${API}/eos/meetings/${meetingId}/complete`);
      setIsTimerRunning(false);
      toast.success('Meeting completed!');
      
      if (copilotEnabled) {
        setShowUpdatesDialog(true);
      } else {
        navigate('/eos/l10-meetings');
      }
    } catch (error) {
      toast.error('Failed to complete meeting');
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentSectionInfo = () => agendaSections.find(s => s.key === currentSection) || agendaSections[0];
  const getSectionProgress = () => {
    const section = getCurrentSectionInfo();
    const maxTime = section.defaultDuration * 60;
    return Math.min((sectionTimer / maxTime) * 100, 100);
  };
  const getCurrentPresenter = () => currentPresenterIndex >= 0 && currentPresenterIndex < presenters.length ? presenters[currentPresenterIndex] : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 bg-background rounded-lg shadow-lg p-4 z-50 border-2 border-primary">
        <div className="flex items-center gap-4">
          <Clock className="h-5 w-5" />
          <div>
            <div className="font-semibold">{formatTime(overallTimer)}</div>
            <div className="text-sm text-muted-foreground">{getCurrentSectionInfo().label}</div>
          </div>
          <Button size="sm" onClick={() => setIsMinimized(false)}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const currentSectionInfo = getCurrentSectionInfo();
  const currentPresenter = getCurrentPresenter();
  const sectionIndex = agendaSections.findIndex(s => s.key === currentSection);
  const currentAiNotes = aiSectionNotes[currentSection];

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : ''} bg-background min-h-screen`}>
      {/* Header */}
      <div className="bg-card border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/eos/l10-meetings')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Exit
              </Button>
              
              <div>
                <h1 className="text-lg font-bold">{meeting?.title || 'L10 Meeting'}</h1>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{new Date(meeting?.scheduled_date).toLocaleDateString()}</span>
                  {copilotEnabled && (
                    <Badge className="bg-purple-600 text-xs py-0">
                      <Sparkles className="h-3 w-3 mr-1" />
                      AI Copilot ON
                    </Badge>
                  )}
                  {captureEnabled && (
                    <Badge variant="destructive" className="text-xs py-0">
                      <Circle className="h-2 w-2 mr-1 fill-current animate-pulse" />
                      Recording
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Header Controls */}
            <div className="flex items-center gap-3">
              {/* Video Controls */}
              {videoEnabled ? (
                <>
                  {videoJoined ? (
                    <div className="flex items-center gap-1">
                      <Badge className="bg-green-600">
                        <Circle className="h-2 w-2 mr-1 fill-current animate-pulse" />
                        In Call
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => setShowVideoPanel(!showVideoPanel)}
                      >
                        {showVideoPanel ? (
                          <PanelRightClose className="h-4 w-4" />
                        ) : (
                          <PanelRight className="h-4 w-4" />
                        )}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={handleLeaveVideo}
                      >
                        <Phone className="h-4 w-4 mr-1 rotate-[135deg]" />
                        Leave
                      </Button>
                    </div>
                  ) : (
                    <Button variant="default" className="bg-green-600 hover:bg-green-700" size="sm" onClick={handleJoinVideo}>
                      <Video className="h-4 w-4 mr-1" />
                      Join Video
                    </Button>
                  )}
                </>
              ) : isFacilitator && (
                <Button variant="outline" size="sm" onClick={handleEnableVideo}>
                  <Video className="h-4 w-4 mr-1" />
                  Enable Video
                </Button>
              )}
              
              {/* AI Copilot Toggle */}
              {!copilotEnabled && isFacilitator && (
                <Button variant="outline" size="sm" onClick={handleEnableCopilot}>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Enable AI Copilot
                </Button>
              )}
              
              {/* Timer */}
              <div className="text-center px-3 py-1 bg-muted rounded-lg">
                <div className="text-xl font-bold font-mono">{formatTime(overallTimer)}</div>
              </div>

              <div className="flex items-center gap-1">
                {isPaused ? (
                  <Button onClick={togglePause} size="sm">
                    <PlayCircle className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button onClick={togglePause} variant="outline" size="sm">
                    <PauseCircle className="h-4 w-4" />
                  </Button>
                )}
                
                <Button variant="outline" size="sm" onClick={() => setIsMinimized(true)}>
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="grid grid-cols-12 gap-3">
          {/* Left Sidebar */}
          <div className="col-span-3 space-y-3">
            {/* Agenda */}
            <Card>
              <CardHeader className="py-2">
                <CardTitle className="text-sm">Agenda</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 py-0 pb-2">
                {agendaSections.map((section, index) => {
                  const isCurrent = section.key === currentSection;
                  const isPast = sectionIndex > index;
                  
                  return (
                    <button
                      key={section.key}
                      onClick={() => isFacilitator && setCurrentSection(section.key)}
                      disabled={!isFacilitator}
                      className={`w-full text-left p-1.5 rounded-lg transition-all text-xs ${
                        isCurrent ? 'bg-primary/10 border border-primary' : isPast ? 'bg-muted/50 opacity-60' : 'hover:bg-muted/50'
                      } ${!isFacilitator && 'cursor-default'}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded ${section.color} text-white`}>
                          <section.icon className="h-3 w-3" />
                        </div>
                        <span className="flex-1 truncate">{section.label}</span>
                        {isPast && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Participants */}
            <Card>
              <CardHeader className="py-2">
                <CardTitle className="text-xs">Participants ({presenters.length})</CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-2">
                <ScrollArea className="h-[150px]">
                  <div className="space-y-1">
                    {presenters.map((participant, idx) => {
                      const isCurrentPresenter = idx === currentPresenterIndex;
                      const hasCompleted = completedPresenters.includes(participant.user_id);
                      
                      return (
                        <div key={participant.user_id || idx} className={`flex items-center gap-2 p-1.5 rounded-lg ${isCurrentPresenter ? 'bg-primary/10 border border-primary' : hasCompleted ? 'bg-green-50 dark:bg-green-950/30' : ''}`}>
                          <UserAvatar user={participant} size="xs" />
                          <span className="text-xs truncate flex-1">{participant.display_name}</span>
                          {isCurrentPresenter && <Hand className="h-3 w-3 text-blue-600" />}
                          {hasCompleted && !isCurrentPresenter && <UserCheck className="h-3 w-3 text-green-500" />}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="col-span-6">
            {/* Presenter + Timer */}
            <Card className={`mb-3 ${isMyTurn ? 'border-2 border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}>
              <CardContent className="py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {currentPresenter && <UserAvatar user={currentPresenter} size="sm" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <currentSectionInfo.icon className="h-4 w-4" />
                        <span className="font-semibold">{currentSectionInfo.label}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {currentPresenter?.display_name || 'No presenter'} • {currentPresenterIndex + 1}/{presenters.length}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className={`text-xl font-bold font-mono ${getSectionProgress() > 100 ? 'text-red-500' : ''}`}>
                        {formatTime(sectionTimer)}
                      </div>
                      <Progress value={getSectionProgress()} className="h-1 w-20" />
                    </div>
                    
                    {!isObserver && (
                      <div className="flex gap-1">
                        {(isMyTurn || isFacilitator) && (
                          <Button size="sm" onClick={handleNextPerson}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        )}
                        {isFacilitator && (
                          <Button variant="outline" size="sm" onClick={handleSkipPerson}>
                            <SkipForward className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {tangentWarning && (
                  <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center gap-2">
                    <Bell className="h-4 w-4 text-yellow-600 animate-pulse" />
                    <span className="text-xs font-semibold text-yellow-800 dark:text-yellow-200">⚠️ 80% time used!</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section Content */}
            <Card>
              <CardContent className="py-3">
                <L10SectionContent 
                  section={currentSection} 
                  data={sectionData[currentSection]} 
                  onGenerateSummary={handleGenerateExecutiveSummary}
                  onGenerateUpdates={handleGenerateSuggestedUpdates}
                  aiLoading={aiLoading}
                  copilotEnabled={copilotEnabled}
                  meetingId={meetingId}
                />
              </CardContent>
            </Card>

            {/* Section Navigation */}
            {(isFacilitator || currentUser?.role === 'super_admin') && (
              <div className="mt-3 flex justify-between">
                <Button onClick={goToPreviousSection} disabled={sectionIndex === 0} variant="outline" size="sm">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>

                {currentSection === 'conclude' ? (
                  <Button onClick={completeMeeting} className="bg-green-600 hover:bg-green-700" size="sm">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Complete Meeting
                  </Button>
                ) : (
                  <Button onClick={goToNextSection} size="sm">
                    Next Section
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar - Video & AI Copilot */}
          <div className="col-span-3 space-y-3">
            {/* Video Panel - Show when joined */}
            {videoJoined && showVideoPanel && (
              <L10VideoPanel
                meetingId={meetingId}
                isJoined={videoJoined}
                onJoin={handleJoinVideo}
                onLeave={handleLeaveVideo}
                participants={presenters}
                currentUserId={currentUser?.id || currentUser?.user_id}
                currentSection={currentSection}
                isFacilitator={isFacilitator}
                copilotEnabled={copilotEnabled}
                videoNotes={videoNotes}
                onNotesUpdate={handleVideoNotesUpdate}
              />
            )}
            
            {/* Video Not Joined - Show Join Prompt when video is enabled */}
            {videoEnabled && !videoJoined && (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="py-6 text-center">
                  <Video className="h-12 w-12 mx-auto mb-3 text-slate-600" />
                  <h3 className="text-sm font-semibold text-white mb-2">Video Available</h3>
                  <p className="text-xs text-slate-400 mb-4">Join the video call to collaborate with your team</p>
                  <Button onClick={handleJoinVideo} className="bg-green-600 hover:bg-green-700">
                    <Video className="h-4 w-4 mr-2" />
                    Join Video
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Enhanced AI Copilot Panel */}
            <AICopilotPanel
              meetingId={meetingId}
              currentSection={currentSection}
              copilotEnabled={copilotEnabled}
              isFacilitator={isFacilitator}
              onEnableCopilot={handleEnableCopilot}
              sectionData={sectionData}
              participants={presenters}
            />
            
            {/* Phase B: Live Transcription */}
            {copilotEnabled && (
              <AudioCapture
                meetingId={meetingId}
                currentSection={currentSection}
                currentSpeaker={presenters[currentPresenterIndex]?.display_name}
                enabled={transcriptionEnabled}
                onEnabledChange={setTranscriptionEnabled}
                onTranscriptionReceived={(t) => {
                  setTranscriptions(prev => [...prev, t]);
                  toast.info('New transcription captured');
                }}
              />
            )}
            
            {/* Phase C: AI Suggestions Queue */}
            {copilotEnabled && (
              <SuggestionQueue
                meetingId={meetingId}
                autoApplyMode={autoApplyMode}
                onSuggestionApplied={(suggestion, entityId) => {
                  toast.success(`${suggestion.type} created from AI suggestion`);
                }}
              />
            )}
            
            {/* Quick Actions */}
            {copilotEnabled && (
              <Card>
                <CardContent className="py-2 space-y-2">
                  <Button variant="outline" size="sm" className="w-full" onClick={handleGenerateExecutiveSummary} disabled={aiLoading}>
                    <FileText className="h-4 w-4 mr-1" />
                    Executive Summary
                  </Button>
                  <Button variant="outline" size="sm" className="w-full" onClick={handleGenerateSuggestedUpdates} disabled={aiLoading}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Review Updates
                  </Button>
                  <Button variant="outline" size="sm" className="w-full" onClick={handlePrepareEmail} disabled={aiLoading}>
                    <Mail className="h-4 w-4 mr-1" />
                    Email Summary
                  </Button>
                  {isFacilitator && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full" 
                      onClick={() => setShowAutoApplySettings(true)}
                    >
                      <Settings2 className="h-4 w-4 mr-1" />
                      Auto-Apply Settings
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {/* Executive Summary Dialog */}
      <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Executive Summary
            </DialogTitle>
          </DialogHeader>
          
          {aiSummary && (
            <div className="space-y-4">
              {aiSummary.executive_summary?.highlights?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">📋 Highlights</h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {aiSummary.executive_summary.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
              
              {aiSummary.decisions?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">✅ Decisions</h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {aiSummary.decisions.map((d, i) => (
                      <li key={i}>{d.decision} {d.owner && `(${d.owner})`}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {aiSummary.coaching?.at_risk_people?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-orange-600">⚠️ At-Risk</h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {aiSummary.coaching.at_risk_people.map((p, i) => (
                      <li key={i}>{p.name}: {p.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {aiSummary.meeting_quality_score && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Meeting Quality Score</span>
                    <Badge>{aiSummary.meeting_quality_score.score}/10</Badge>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSummaryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Enhanced Review Updates Dialog */}
      <ReviewApplyDialog
        open={showUpdatesDialog}
        onOpenChange={setShowUpdatesDialog}
        meetingId={meetingId}
        suggestions={suggestedUpdates}
        onSuggestionsUpdate={setSuggestedUpdates}
        onComplete={() => { 
          setShowUpdatesDialog(false); 
          navigate('/eos/l10-meetings'); 
        }}
      />
      
      {/* Email Summary Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Meeting Summary
            </DialogTitle>
          </DialogHeader>
          
          {emailPayload && (
            <div className="space-y-4">
              <div>
                <Label>Subject</Label>
                <div className="p-2 bg-muted rounded-lg text-sm">
                  {emailPayload.email_content?.subject}
                </div>
              </div>
              
              <div>
                <Label>Recipients ({emailPayload.recipients?.length || 0})</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {emailPayload.recipients?.map((r, i) => (
                    <Badge key={i} variant="outline">{r.name || r.email}</Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Content Preview</Label>
                <ScrollArea className="h-[200px] mt-1 border rounded-lg p-3">
                  {emailPayload.email_content?.sections?.map((section, i) => (
                    <div key={i} className="mb-4">
                      <h4 className="font-semibold">{section.title}</h4>
                      <ul className="list-disc list-inside text-sm mt-1">
                        {section.items?.map((item, j) => <li key={j}>{item}</li>)}
                      </ul>
                    </div>
                  ))}
                </ScrollArea>
              </div>
              
              <div className="p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-2 text-yellow-600" />
                Email will be queued. Actual delivery when email integration is configured.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail}>
              <Mail className="h-4 w-4 mr-2" />
              Queue Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Phase C: Auto-Apply Settings Dialog */}
      <AutoApplySettings
        meetingId={meetingId}
        open={showAutoApplySettings}
        onOpenChange={setShowAutoApplySettings}
        onSettingsUpdated={(settings) => {
          setAutoApplyMode(settings.mode);
          toast.success('Auto-apply settings updated');
        }}
      />
    </div>
  );
};

export default EOSL10MeetingRoom;
